// Household member roles — the ONE vocabulary for the role a person
// holds inside their household (primary / spouse / child / other /
// member).
//
// Why this module exists (Donna, 2026-07-31): the Cleanup drawer told
// her "open the extra member below and set its role to spouse / child /
// other" and there was no such control anywhere in the OS. Building one
// meant a sixth inline copy of the role union and a fifth copy of the
// "humanize" helper, so the vocabulary got a home instead.
//
// Root-safe by construction: imports only the HouseholdRole type, so
// both the Worker runtime and the React bundle can pull it in.
//
// KNOWN REMAINING DUPLICATES — deliberately left alone, because this
// change doesn't otherwise touch them and repointing them would widen
// the diff for no behavioral gain:
//   * web/src/components/ContactMoveHouseholdModal.tsx (its own Role +
//     ROLE_OPTIONS — the labels below were seeded FROM it)
//   * web/src/components/service-records/TransactionLinkedWorkPanel.tsx
//     (its own humanizeRole)
// A future session consolidating those should delete them in favor of
// this module, not add a third pattern.

import type { HouseholdRole } from "../types/enums";

export interface HouseholdRoleOption {
  value: HouseholdRole;
  label: string;
  /** Plain-English "when is this the right answer" for the picker. */
  hint: string;
}

// Display order for a role picker. "Spouse" leads because the
// multi-primary households this was built for are overwhelmingly
// married couples imported from ActiveCampaign with BOTH spouses
// carrying household_role='primary'.
export const HOUSEHOLD_ROLE_OPTIONS: ReadonlyArray<HouseholdRoleOption> = [
  {
    value: "spouse",
    label: "Spouse",
    hint: "Married or partnered to the primary contact.",
  },
  {
    value: "child",
    label: "Child",
    hint: "A son or daughter in this household.",
  },
  {
    value: "other",
    label: "Other",
    hint: "Another relative or dependent — a parent, sibling, or in-law.",
  },
  {
    value: "member",
    label: "Member",
    hint: "In the household, with no more specific relationship recorded.",
  },
  {
    value: "primary",
    label: "Primary contact",
    hint: "The one primary contact. Whoever holds it today becomes a regular member.",
  },
];

const LABELS: Record<HouseholdRole, string> = {
  primary: "Primary contact",
  spouse: "Spouse",
  child: "Child",
  other: "Other",
  member: "Member",
};

/**
 * "spouse" → "Spouse". An unrecognized value is humanized generically
 * (underscores to spaces, first letter capitalized) rather than dropped
 * — a role that predates the enum still has to render as SOMETHING the
 * reader can understand, and silently blanking it would hide data.
 */
export function humanizeHouseholdRole(role: string | null | undefined): string {
  if (role == null) return "";
  const raw = role.trim();
  if (raw === "") return "";
  const known = LABELS[raw.toLowerCase() as HouseholdRole];
  if (known) return known;
  const r = raw.replace(/_/g, " ").toLowerCase();
  return r.charAt(0).toUpperCase() + r.slice(1);
}

/** Type guard for an untrusted role string arriving over the wire. */
export function isHouseholdRole(value: unknown): value is HouseholdRole {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(LABELS, value)
  );
}
